import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Identity as a queryable resource',
    description:
      'A clean REST surface over the unified identity graph. Users, groups, entitlements, devices: all addressable, filterable, paginated.',
    bullets: [
      '/users, /groups, /entitlements, /devices',
      'OData-style filters and projections',
      'Cursor-based pagination',
      'Predictable error model and HTTP semantics',
    ],
  },
  {
    title: 'Write-back, not just read',
    description:
      'Create, update, and disable identities through the API. Changes propagate downstream through the same connector mesh that powers sync.',
    bullets: [
      'POST / PATCH / DELETE on identity objects',
      'Idempotency keys for safe retries',
      'Bulk operations with partial-failure semantics',
      'Auto-propagation through connectors',
    ],
  },
  {
    title: 'Webhooks for live integration',
    description:
      'Subscribe to identity events (joiner, mover, leaver, attribute change) and drive downstream workflows in real time.',
    bullets: [
      'Configurable event subscriptions',
      'HMAC-signed payloads',
      'Replay window for dropped deliveries',
      'Per-subscriber dead-letter queue',
    ],
  },
  {
    title: 'Stable, versioned, documented',
    description:
      'Versioned endpoints with deprecation policy. OpenAPI spec, code samples, and a CLI you can pipe into your CI.',
    bullets: [
      'OpenAPI 3 spec, downloadable',
      'Client SDKs for Go, Python, Java, .NET',
      'CLI for tenants and users as code',
      'Sandbox environment for testing',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Authenticate',
    description:
      'Provision a service account, scope an OAuth client, or use a signed JWT. Token TTLs and scopes are policy-controlled.',
  },
  {
    title: 'Read or write',
    description:
      'GET the identity graph, POST changes, or subscribe to webhook events; same shape across every resource.',
  },
  {
    title: 'Propagate downstream',
    description:
      'Writes flow through Monosync connectors to AD, Entra, Workday, and every other connected system automatically.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monosync Connectors',
    description:
      'The downstream propagation mesh the API drives.',
    href: '/sync/connectors',
  },
  {
    label: 'Monosync Governance',
    description:
      'Entitlement mapping and access reviews on top of the graph.',
    href: '/sync/governance',
  },
  {
    label: 'Developer documentation',
    description:
      'Reference docs, SDKs, and integration guides.',
    href: 'https://help.monofor.com',
  },
]

export default function ApiPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Monosync API: identity as data"
        description="Clean REST API over the unified identity graph. Read and write users, groups, and entitlements. Webhooks for live event-driven integration."
        path="/sync/api"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosync · API"
          title={
            <>
              Identity as data.
              <br />
              Queryable. Writable.
            </>
          }
          body="A clean, versioned REST API over the unified identity graph. Read users, groups, entitlements, and devices. Write changes that propagate through connectors. Subscribe to events. Build with it."
          badges={[
            'REST + OpenAPI 3',
            'Webhooks and SDKs',
            'Cursor pagination',
          ]}
        />
        <SolutionPillars
          title={<>The identity layer your platform team will actually use.</>}
          body="Most identity APIs feel bolted on. Monosync's API is the same surface internal teams build against, so what you script today is what we ship tomorrow."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Authenticate. Operate. Propagate.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
